import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {typeToName} from 'util/NameUtil';

import {AnalyticsParameterConfigGraphQL} from '../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {AnalyticsParameterValue} from './__generated/AnalyticsParameterValue';
import {ParameterType} from './ParameterTypes';

const ANALYTICS_PARAMETER_VALUE_FRAGMENT: DocumentNode = gql`
  fragment AnalyticsParameterValue on AnalyticsParameterValueNode {
    config {
      id
    }
    valueJson
  }
`;

export const AnalyticsParameterValueFragment: FragmentWrapper<AnalyticsParameterValue> = {
  fragment: ANALYTICS_PARAMETER_VALUE_FRAGMENT,
  fragmentName: 'AnalyticsParameterValue',
};

export type ConfigTypename = AnalyticsParameterConfigGraphQL['__typename'];

export const CONFIG_TYPENAME_TO_PARAMETER_TYPE: {[k in ConfigTypename]: ParameterType} = {
  AnalyticsParameterBioentityLinkConfigNode: ParameterType.BIOENTITY_LINK,
  AnalyticsParameterDateConfigNode: ParameterType.DATE,
  AnalyticsParameterEntryLinkConfigNode: ParameterType.ENTRY_LINK,
  AnalyticsParameterFolderLinkConfigNode: ParameterType.FOLDER_LINK,
  AnalyticsParameterNumberConfigNode: ParameterType.NUMBER,
  AnalyticsParameterProcessLinkConfigNode: ParameterType.PROCESS_LINK,
  AnalyticsParameterProcessTaskLinkConfigNode: ParameterType.PROCESS_TASK_LINK,
  AnalyticsParameterProjectLinkConfigNode: ParameterType.PROJECT_LINK,
  AnalyticsParameterSelectorConfigNode: ParameterType.SELECTOR,
  AnalyticsParameterStorableLinkConfigNode: ParameterType.STORABLE_LINK,
  AnalyticsParameterStringConfigNode: ParameterType.STRING,
  AnalyticsParameterStudyLinkConfigNode: ParameterType.STUDY_LINK,
  AnalyticsParameterTeamLinkConfigNode: ParameterType.TEAM_LINK,
  AnalyticsParameterUserLinkConfigNode: ParameterType.USER_LINK,
};

export const PARAMETER_TYPE_TO_CONFIG_TYPENAME: {[k in ParameterType]: ConfigTypename} = {
  [ParameterType.BIOENTITY_LINK]: 'AnalyticsParameterBioentityLinkConfigNode',
  [ParameterType.DATE]: 'AnalyticsParameterDateConfigNode',
  [ParameterType.ENTRY_LINK]: 'AnalyticsParameterEntryLinkConfigNode',
  [ParameterType.FOLDER_LINK]: 'AnalyticsParameterFolderLinkConfigNode',
  [ParameterType.NUMBER]: 'AnalyticsParameterNumberConfigNode',
  [ParameterType.PROCESS_LINK]: 'AnalyticsParameterProcessLinkConfigNode',
  [ParameterType.PROCESS_TASK_LINK]: 'AnalyticsParameterProcessTaskLinkConfigNode',
  [ParameterType.PROJECT_LINK]: 'AnalyticsParameterProjectLinkConfigNode',
  [ParameterType.SELECTOR]: 'AnalyticsParameterSelectorConfigNode',
  [ParameterType.STORABLE_LINK]: 'AnalyticsParameterStorableLinkConfigNode',
  [ParameterType.STRING]: 'AnalyticsParameterStringConfigNode',
  [ParameterType.STUDY_LINK]: 'AnalyticsParameterStudyLinkConfigNode',
  [ParameterType.TEAM_LINK]: 'AnalyticsParameterTeamLinkConfigNode',
  [ParameterType.USER_LINK]: 'AnalyticsParameterUserLinkConfigNode',
};

// Keep in sync with back-end PARAMETER_TYPE_TO_READABLE
export const PARAMETER_TYPE_TO_READABLE: {[key in ParameterType]: string} = {
  [ParameterType.BIOENTITY_LINK]: 'entity',
  [ParameterType.DATE]: 'date',
  [ParameterType.ENTRY_LINK]: 'entry',
  [ParameterType.FOLDER_LINK]: 'folder',
  [ParameterType.NUMBER]: 'number',
  [ParameterType.PROCESS_LINK]: typeToName(ModelTypeConstants.PROCESS),
  [ParameterType.PROCESS_TASK_LINK]: 'workflow task',
  [ParameterType.PROJECT_LINK]: 'project',
  [ParameterType.SELECTOR]: 'dropdown',
  [ParameterType.STORABLE_LINK]: 'inventory',
  [ParameterType.STRING]: 'text',
  [ParameterType.STUDY_LINK]: 'study',
  [ParameterType.TEAM_LINK]: 'team',
  [ParameterType.USER_LINK]: 'user',
};

// Must be sorted according to the display name, to keep the menu sorted
export const PRIMITIVE_PARAMETER_TYPES = [ParameterType.DATE, ParameterType.NUMBER, ParameterType.STRING];
export const LINK_PARAMETER_TYPES = [
  ParameterType.SELECTOR,
  ParameterType.BIOENTITY_LINK,
  ParameterType.ENTRY_LINK,
  ParameterType.FOLDER_LINK,
  ParameterType.PROCESS_LINK,
  ParameterType.PROCESS_TASK_LINK,
  ParameterType.PROJECT_LINK,
  ParameterType.STORABLE_LINK,
  ParameterType.STUDY_LINK,
  ParameterType.TEAM_LINK,
  ParameterType.USER_LINK,
];

// keep in sync with https://github.com/benchling/aurelia/blob/dev/benchling/insights/parameters/constants.py
export const PARAMETER_TYPE_ALLOWS_MULTIPLE_VALUES: {[k in ParameterType]: boolean} = {
  [ParameterType.BIOENTITY_LINK]: true,
  [ParameterType.DATE]: false,
  [ParameterType.ENTRY_LINK]: true,
  [ParameterType.FOLDER_LINK]: false,
  [ParameterType.NUMBER]: false,
  [ParameterType.PROCESS_LINK]: true,
  [ParameterType.PROCESS_TASK_LINK]: true,
  [ParameterType.PROJECT_LINK]: false,
  [ParameterType.SELECTOR]: true,
  [ParameterType.STORABLE_LINK]: true,
  [ParameterType.STRING]: false,
  [ParameterType.TEAM_LINK]: true,
  [ParameterType.USER_LINK]: true,
  [ParameterType.STUDY_LINK]: true,
};
